import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiMessageSquare, FiTrendingUp, FiBarChart, FiCheckCircle, FiDollarSign, FiShield, FiSmile, FiZap } = FiIcons;

const TransformationSection = () => {
  const transformations = [
    {
      icon: FiTarget,
      title: "Leads come in automatically",
      description: "No more chasing prospects or cold calling",
      color: "blue"
    },
    {
      icon: FiMessageSquare,
      title: "WhatsApp replies go out instantly",
      description: "24/7 automated responses to customer inquiries",
      color: "green"
    },
    {
      icon: FiTrendingUp,
      title: "Follow-ups happen without your involvement",
      description: "AI nurtures leads while you focus on closing deals",
      color: "purple"
    },
    {
      icon: FiBarChart,
      title: "Sales pipeline updates itself",
      description: "Real-time tracking of every lead and opportunity",
      color: "indigo"
    },
    {
      icon: FiCheckCircle,
      title: "You know exactly which lead is ready to buy",
      description: "Smart scoring identifies hot prospects automatically",
      color: "emerald"
    },
    {
      icon: FiZap,
      title: "You close more deals in less time",
      description: "Streamlined process from lead to conversion",
      color: "yellow"
    },
    {
      icon: FiDollarSign,
      title: "You save money on agencies",
      description: "No more expensive marketing agency fees",
      color: "red"
    },
    {
      icon: FiShield,
      title: "Your brand looks premium",
      description: "Professional presence across all touchpoints",
      color: "blue"
    },
    {
      icon: FiSmile,
      title: "Your revenue becomes stable",
      description: "Predictable income through systematic processes",
      color: "green"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Imagine Running Your Business 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-400"> Like This…</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Transform your daily business operations from stressful chaos to smooth, automated success
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {transformations.map((transformation, index) => (
            <motion.div
              key={index}
              className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02, y: -5 }}
            >
              <div className="flex items-center mb-6">
                <div className={`w-14 h-14 bg-gradient-to-r from-${transformation.color}-400 to-${transformation.color}-600 rounded-xl flex items-center justify-center mr-4`}>
                  <SafeIcon icon={transformation.icon} className="w-7 h-7 text-white" />
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-white mb-4">{transformation.title}</h3>
              <p className="text-gray-300 leading-relaxed">{transformation.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Emotional Closing */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-yellow-500/10 to-pink-500/10 border border-yellow-500/30 rounded-3xl p-12 max-w-5xl mx-auto backdrop-blur-lg">
            <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
              "You focus on <span className="text-yellow-400">business growth</span>. 
              <br />FunnelsDone handles everything else."
            </h3>
            
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Stop being a slave to your business. Start being the CEO who strategizes growth while systems handle operations.
            </p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="https://funnelsdone.com/cash" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-xl rounded-full hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/25"
              >
                👉 Start Your Transformation Today
                <SafeIcon icon={FiZap} className="ml-3 w-6 h-6" />
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TransformationSection;